import classNames from "classnames";
import { TransactionsPairForReconciliation } from "@common/contracts/reconciliation.contract";
import { useDroppable } from "@dnd-kit/core";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { memo, useCallback, useContext, useMemo } from "react";
import { EqualIcon } from "@icons";
import {
    getStatmentTransactionId,
    pairHasDifference,
} from "@common/helpers/reconciliation";
import { canCashItemClearOnDate } from "@common/helpers/reconciliationCashItems";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import { GridTableRow } from "@design-system/Tables/GridTable/GridTable";
import styles from "./Reconciliation.module.scss";
import { ReconciliationTransaction } from "./ReconciliationTransaction";
import { ReconciliationAction } from "./ReconciliationAction";
import {
    pairDifferenceIsAccepted,
    RECONCILIATION_ROW_CLASSNAME,
} from "./helpers";
import { ReconciliationAccountTransaction } from "./ReconciliationAccountTransaction";
import { ReconciliationContext } from "./Reconciliation.context";

interface Props {
    pair: TransactionsPairForReconciliation;
    index: number;
    isLastBeforeStatement?: boolean;
    isLastInStatement?: boolean;
    onUpdatePair: (pair: TransactionsPairForReconciliation) => void;
    isBalanceReconciliation: boolean;
    draggedAmount?: number;
    draggedCashItemDate?: string;
    draggedId?: string | number;
    overItemId?: string | number;
    measureElement?: (element: HTMLElement | null) => void;
}

const ReconciliationPairComponent: React.FC<Props> = ({
    pair,
    index,
    isLastBeforeStatement,
    isLastInStatement,
    onUpdatePair,
    isBalanceReconciliation,
    draggedAmount,
    draggedCashItemDate,
    draggedId,
    overItemId,
    measureElement,
}) => {
    const {
        financialTransactionsToRemoveIdsSet,
        statementTransactionsToAddIdsSet,
        notReconciledFinancialTransactionsIdsSet,
        financialTransactionsFromPreviousPeriodIds,
    } = useContext(ReconciliationContext);
    const hasDifference = pairHasDifference(pair);
    const hasPendingDifference =
        hasDifference &&
        !pairDifferenceIsAccepted({
            pair,
            financialTransactionsToRemoveIdsSet,
            statementTransactionsToAddIdsSet,
            notReconciledFinancialTransactionsIdsSet,
            financialTransactionsFromPreviousPeriodIds,
        });
    const dropDisabled =
        (!pair.fromStatement ||
            draggedAmount !== pair.fromStatement?.amount ||
            (draggedCashItemDate !== undefined &&
                !canCashItemClearOnDate({
                    cashItemDateOnly: draggedCashItemDate,
                    clearedDateOnly: pair.fromStatement.date,
                }))) &&
        draggedId !== pair.fromAccount?.financialTransactionId;
    const isOver =
        pair.fromStatement &&
        overItemId === getStatmentTransactionId(pair.fromStatement);

    const { setNodeRef: setDroppableRef } = useDroppable({
        id: pair.fromStatement
            ? getStatmentTransactionId(pair.fromStatement)
            : "",
        disabled: dropDisabled,
    });

    const setNodeRef = useCallback(
        (node: HTMLElement | null) => {
            setDroppableRef(node);
            measureElement?.(node);
        },
        [setDroppableRef, measureElement],
    );

    const linkIcon = useMemo(() => {
        if (pair.fromStatement && pair.fromAccount) {
            return <EqualIcon className="icon-color-gray-500" />;
        }
        return null;
    }, [pair.fromStatement, pair.fromAccount]);

    const linkIconTooltip = useMemo(() => {
        if (pair.fromStatement && pair.fromAccount) {
            return "Transaction from statement found in Kick";
        }
        return null;
    }, [pair.fromStatement, pair.fromAccount]);

    return (
        <GridTableRow
            columnBorders
            className={classNames(
                RECONCILIATION_ROW_CLASSNAME,
                styles.pairRow,
                {
                    [styles.rowWithPendingDifference]: hasPendingDifference,
                    [styles.nonDroppableRow]: !!draggedId && dropDisabled,
                    [styles.dropOverRow]: isOver,
                    [styles.lastBeforeStatement]: isLastBeforeStatement,
                    [styles.lastInStatement]: isLastInStatement,
                },
            )}
            setNodeRef={setNodeRef}
            data-index={index}
        >
            <ReconciliationTransaction
                transaction={pair.fromStatement}
                linkIcon={
                    <Tooltip content={linkIconTooltip}>
                        <span className={styles.linkIcon}>{linkIcon}</span>
                    </Tooltip>
                }
            />
            <ReconciliationAccountTransaction
                pair={pair}
                isDragging={!!draggedId}
            />
            <div className={styles.actionsCell}>
                <ReconciliationAction
                    pair={pair}
                    onUpdatePair={onUpdatePair}
                    isBalanceReconciliation={isBalanceReconciliation}
                />
            </div>
        </GridTableRow>
    );
};

// Memoized so unrelated parent re-renders (e.g. footer toolbar state,
// `focusRequest` bumps) skip re-rendering the ~30 mounted pairs. Default
// shallow compare suffices: `pair` is a stable ref from the summary, the
// drag props are primitives, and `onUpdatePair` is `useCallback`-stable
// in `ReconciliationMatchStep`.
export const ReconciliationPair = memo(ReconciliationPairComponent);
